import * as Phaser from 'phaser';
import { Client, Room } from 'colyseus.js';
import { COLYSEUS_URL } from '../config/network';
import { REFERENCE_WIDTH, REFERENCE_HEIGHT } from '../config/game.config';
import { HomeHUD } from '../ui/HomeHUD';
import { CookingPanel } from '../ui/CookingPanel';
import { RecipeDiscoveryToast } from '../ui/RecipeDiscoveryToast';
import { computeEggGlow, type EggGlowParams } from '../ui/petEggGlow';
import {
  MSG_SEED_SNAPSHOT,
  MSG_SEED_HARVEST,
  type SeedRecord,
  type SeedSnapshotEvent,
  type SeedHarvestResult,
} from '@game/shared';
import {
  computePlotCell,
  cellCenter,
  GARDEN_LAYOUT,
  type PlotCellState,
  type GrowthStage,
} from '../garden/gardenPlotLogic';

export const HOME_SCENE_KEY = 'HomeScene' as const;

export interface HomeSceneData {
  characterId: string;
  ownerId: string;
}

const SPAWN_X = 320;
const SPAWN_Y = 240;

// ── Painted interior palette ────────────────────────────────────────────────
const STONE_WALL = 0x8b7355;
const WARM_WOOD = 0xa0784c;
const HEARTH_GLOW = 0xc4825a;
const SHADOW_BROWN = 0x4a3728;
const DEEP_SHADOW = 0x2e1f14;
const FLOOR_STONE = 0x6b5b4a;
const FLOOR_DARK = 0x564838;
const WINDOW_LIGHT = 0xe8d4a0;
const SHELF_WOOD = 0x8b6914;
const BED_FABRIC = 0x7a6248;
const BED_BLANKET = 0x9c7c5c;
const DOOR_WOOD = 0x6b4226;
const DOOR_FRAME = 0x8b5e3c;
const EMBER_ORANGE = 0xff8844;
const EMBER_YELLOW = 0xffcc44;
const EMBER_RED = 0xdd5533;

// ── Stage-specific upgrades ─────────────────────────────────────────────────
const STAGE_HAS_LOFT: Record<string, boolean> = {
  foundation: false,
  cottage: true,
  house: true,
  manor: true,
};

// Garden visual colors (SMA-70 F7)
const SOIL_COLOR = 0x6b4226;
const SPROUT_STEM_COLOR = 0x3d8b37;
const SAPLING_TRUNK_COLOR = 0x5c3d1e;
const SAPLING_LEAF_COLOR = 0x4aa84a;
const BLOOM_PETAL_COLOR = 0xe88bc4;
const BLOOM_CENTER_COLOR = 0xf5d742;
const GARDEN_BG_COLOR = 0x3d2b18;
const GARDEN_BORDER_COLOR = 0x5c4022;

// ── Depth layers ────────────────────────────────────────────────────────────
const DEPTH_BG_WALL = 0;
const DEPTH_MIDGROUND = 2;
const DEPTH_FURNITURE = 3;
const DEPTH_HEARTH = 4;
const DEPTH_FIRE_GLOW = 5;
const DEPTH_EMBERS = 6;
const DEPTH_PLAYER = 10;
const DEPTH_FOREGROUND = 8;

// ── Fire flicker tuning ─────────────────────────────────────────────────────
const FIRE_GLOW_RADIUS = 80;
const FIRE_PULSE_HZ = 1.2;
const FIRE_PULSE_SECONDARY_HZ = 2.7;
const EMBER_COUNT = 6;
const EMBER_SPAWN_INTERVAL_MS = 800;

// ── Dissolve transition ─────────────────────────────────────────────────────
const DISSOLVE_DURATION_MS = 500;

export class HomeScene extends Phaser.Scene {
  private colyseusClient: Client | null = null;
  private homeRoom: Room | null = null;
  private characterId = '';
  private ownerId = '';
  private escKey: Phaser.Input.Keyboard.Key | null = null;
  private exitZone: Phaser.GameObjects.Rectangle | null = null;
  private playerSprite: Phaser.GameObjects.Rectangle | null = null;
  private playerVx = 0;
  private playerVy = 0;
  private cursors: Phaser.Types.Input.Keyboard.CursorKeys | null = null;
  private homeHud: HomeHUD | null = null;
  private cookingPanel: CookingPanel | null = null;
  private recipeToast: RecipeDiscoveryToast | null = null;
  private hearthZone: Phaser.GameObjects.Rectangle | null = null;
  private hearthLabel: Phaser.GameObjects.Text | null = null;
  private cookKey: Phaser.Input.Keyboard.Key | null = null;

  // ── Painted interior graphics layers ────────────────────────────────
  private bgWallGraphics: Phaser.GameObjects.Graphics | null = null;
  private midgroundGraphics: Phaser.GameObjects.Graphics | null = null;
  private foregroundGraphics: Phaser.GameObjects.Graphics | null = null;
  private fireGlowGraphics: Phaser.GameObjects.Graphics | null = null;
  private stageLabel: Phaser.GameObjects.Text | null = null;
  private currentStage = 'foundation';

  // ── Ember particles ─────────────────────────────────────────────────
  private lastEmberSpawnTime = 0;
  private emberPool: Phaser.GameObjects.Arc[] = [];

  // ── Pet egg glow ────────────────────────────────────────────────────
  private eggGlow: Phaser.GameObjects.Graphics | null = null;
  private eggSprite: Phaser.GameObjects.Ellipse | null = null;
  private eggTooltip: Phaser.GameObjects.Text | null = null;
  private eggGlowParams: EggGlowParams | null = null;
  /** Base X for the egg (offset from hearth). */
  private eggBaseX = 0;
  private eggBaseY = 0;

  // ── Garden plot (SMA-70 F7) ─────────────────────────────────────────
  private gardenGraphics: Phaser.GameObjects.Graphics | null = null;
  private gardenTooltip: Phaser.GameObjects.Text | null = null;
  private gardenLabel: Phaser.GameObjects.Text | null = null;
  private harvestKey: Phaser.Input.Keyboard.Key | null = null;
  private seedRecords: SeedRecord[] = [];
  private plotCells: PlotCellState[] = [];
  /** Index of the plot cell the player is near (-1 = none). */
  private nearPlotIndex = -1;

  constructor() {
    super({ key: HOME_SCENE_KEY });
  }

  init(data: HomeSceneData): void {
    this.characterId = data.characterId;
    this.ownerId = data.ownerId;
  }

  create(): void {
    // Set background color for the scene
    this.cameras.main.setBackgroundColor('#1a1a2e');

    // Create graphics layers in depth order
    this.bgWallGraphics = this.add.graphics();
    this.bgWallGraphics.setDepth(DEPTH_BG_WALL);

    this.midgroundGraphics = this.add.graphics();
    this.midgroundGraphics.setDepth(DEPTH_MIDGROUND);

    this.foregroundGraphics = this.add.graphics();
    this.foregroundGraphics.setDepth(DEPTH_FOREGROUND);

    this.fireGlowGraphics = this.add.graphics();
    this.fireGlowGraphics.setDepth(DEPTH_FIRE_GLOW);

    // Render the initial painted interior
    this.renderPaintedInterior('foundation');

    this.playerSprite = this.add.rectangle(SPAWN_X, SPAWN_Y, 16, 24, 0x4488ff);
    this.playerSprite.setDepth(DEPTH_PLAYER);

    // Exit zone -- subtle arch shape drawn by interior renderer
    this.exitZone = this.add.rectangle(SPAWN_X, REFERENCE_HEIGHT - 16, 64, 32, 0x000000, 0);
    this.exitZone.setDepth(0);
    const exitLabel = this.add.text(SPAWN_X, REFERENCE_HEIGHT - 28, 'Exit', {
      fontSize: '10px',
      fontFamily: 'Almendra, serif',
      color: '#eae0c8',
    });
    exitLabel.setOrigin(0.5, 0.5);
    exitLabel.setDepth(DEPTH_FOREGROUND + 1);

    const hearthX = REFERENCE_WIDTH / 2;
    const hearthY = REFERENCE_HEIGHT / 2 - 60;
    // Hearth zone is invisible -- the hearth is drawn by the interior renderer
    this.hearthZone = this.add.rectangle(hearthX, hearthY, 48, 32, 0x000000, 0);
    this.hearthZone.setDepth(DEPTH_HEARTH);
    this.hearthLabel = this.add.text(hearthX, hearthY - 28, '[C] Cook', {
      fontSize: '9px',
      fontFamily: 'Almendra, serif',
      color: '#e8c44a',
    });
    this.hearthLabel.setOrigin(0.5, 0.5);
    this.hearthLabel.setDepth(DEPTH_HEARTH + 1);
    this.hearthLabel.setVisible(false);

    // ── Pet egg: positioned to the right of the hearth ──
    this.eggBaseX = hearthX + 42;
    this.eggBaseY = hearthY + 4;
    this.eggGlow = this.add.graphics();
    this.eggGlow.setDepth(DEPTH_MIDGROUND);
    this.eggGlow.setVisible(false);

    this.eggSprite = this.add.ellipse(this.eggBaseX, this.eggBaseY, 10, 14, 0xf5f0e1);
    this.eggSprite.setDepth(DEPTH_FURNITURE);
    this.eggSprite.setVisible(false);

    this.eggTooltip = this.add.text(this.eggBaseX, this.eggBaseY - 24, '', {
      fontSize: '9px',
      fontFamily: 'Almendra, serif',
      color: '#eae0c8',
      backgroundColor: '#1a1a2e',
      padding: { x: 4, y: 2 },
    });
    this.eggTooltip.setOrigin(0.5, 1);
    this.eggTooltip.setDepth(15);
    this.eggTooltip.setAlpha(0.85);
    this.eggTooltip.setVisible(false);

    // ── Garden plot area (SMA-70 F7) ──
    this.gardenGraphics = this.add.graphics();
    this.gardenGraphics.setDepth(DEPTH_MIDGROUND);

    this.gardenTooltip = this.add.text(0, 0, '', {
      fontSize: '9px',
      fontFamily: 'Almendra, serif',
      color: '#eae0c8',
      backgroundColor: '#1a1a2e',
      padding: { x: 4, y: 2 },
    });
    this.gardenTooltip.setOrigin(0.5, 1);
    this.gardenTooltip.setDepth(15);
    this.gardenTooltip.setAlpha(0.85);
    this.gardenTooltip.setVisible(false);

    const gardenCenterX = GARDEN_LAYOUT.originX +
      (GARDEN_LAYOUT.cols * (GARDEN_LAYOUT.cellW + GARDEN_LAYOUT.gapX) - GARDEN_LAYOUT.gapX) / 2;
    this.gardenLabel = this.add.text(gardenCenterX, GARDEN_LAYOUT.originY - 14, 'Garden', {
      fontSize: '10px',
      fontFamily: 'Almendra, serif',
      color: '#eae0c8',
    });
    this.gardenLabel.setOrigin(0.5, 0.5);
    this.gardenLabel.setDepth(DEPTH_FURNITURE);

    this.cursors = this.input.keyboard!.createCursorKeys();
    this.escKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    this.cookKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.C);
    this.harvestKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.E);

    this.homeHud = new HomeHUD(this);
    this.cookingPanel = new CookingPanel(this);

    // ── Dissolve-in transition ──
    this.cameras.main.setAlpha(0);
    this.tweens.add({
      targets: this.cameras.main,
      alpha: 1,
      duration: DISSOLVE_DURATION_MS,
      ease: 'Sine.easeInOut',
    });

    this.joinHomeRoom();
  }

  update(_time: number, delta: number): void {
    if (!this.playerSprite) return;

    if (Phaser.Input.Keyboard.JustDown(this.escKey!)) {
      this.leaveHome();
      return;
    }

    const speed = 120;
    this.playerVx = 0;
    this.playerVy = 0;
    if (this.cursors!.left.isDown) this.playerVx = -speed;
    else if (this.cursors!.right.isDown) this.playerVx = speed;
    if (this.cursors!.up.isDown) this.playerVy = -speed;
    else if (this.cursors!.down.isDown) this.playerVy = speed;

    const dt = delta / 1000;
    this.playerSprite.x += this.playerVx * dt;
    this.playerSprite.y += this.playerVy * dt;

    this.playerSprite.x = Phaser.Math.Clamp(this.playerSprite.x, 16, REFERENCE_WIDTH - 16);
    this.playerSprite.y = Phaser.Math.Clamp(this.playerSprite.y, 16, REFERENCE_HEIGHT - 16);

    if (this.exitZone && this.playerOverlapsExit()) {
      this.leaveHome();
      return;
    }

    const nearHearth = this.playerNearHearth();
    if (this.hearthLabel) this.hearthLabel.setVisible(nearHearth);

    if (nearHearth && Phaser.Input.Keyboard.JustDown(this.cookKey!)) {
      this.cookingPanel?.toggle();
    }

    // ── Fire flicker update ──
    this.updateFireFlicker(_time);

    // ── Ember particle update ──
    this.updateEmbers(_time);

    // ── Pet egg glow update ──
    this.updateEggGlow(_time);

    // ── Garden plot update (SMA-70 F7) ──
    this.updateGardenPlots();

    // E-key harvest
    if (this.nearPlotIndex >= 0 && Phaser.Input.Keyboard.JustDown(this.harvestKey!)) {
      const cell = this.plotCells[this.nearPlotIndex];
      if (cell && cell.harvestReady) {
        this.requestHarvest(cell.seedRecordId);
      }
    }
  }

  // ── Painted interior rendering ────────────────────────────────────────

  /** Draw the full procedural painted interior for the given home stage. */
  private renderPaintedInterior(stage: string): void {
    this.currentStage = stage;
    const hasLoft = STAGE_HAS_LOFT[stage] ?? false;

    this.drawBackgroundWall(hasLoft);
    this.drawMidgroundFurniture(hasLoft);
    this.drawForegroundElements();

    // Stage label in the Almendra serif style
    if (this.stageLabel) this.stageLabel.destroy();
    const stageDisplay = stage.charAt(0).toUpperCase() + stage.slice(1);
    this.stageLabel = this.add.text(REFERENCE_WIDTH / 2, 18, `Home - ${stageDisplay}`, {
      fontSize: '12px',
      fontFamily: 'Almendra, serif',
      color: '#eae0c8',
    });
    this.stageLabel.setOrigin(0.5, 0.5);
    this.stageLabel.setDepth(DEPTH_FOREGROUND + 2);
    this.stageLabel.setAlpha(0.6);
  }

  /** Background layer: stone walls, floor, ceiling shadow. */
  private drawBackgroundWall(hasLoft: boolean): void {
    const g = this.bgWallGraphics;
    if (!g) return;
    g.clear();

    const margin = 20;
    const left = margin;
    const right = REFERENCE_WIDTH - margin;
    const top = margin;
    const bottom = REFERENCE_HEIGHT - margin;
    const w = right - left;
    const h = bottom - top;

    // Floor -- irregular stone tiles
    g.fillStyle(FLOOR_STONE, 1);
    g.fillRect(left, bottom - h * 0.35, w, h * 0.35);

    // Floor shadow variation (checker-like stone tiles)
    for (let tx = 0; tx < 12; tx++) {
      for (let ty = 0; ty < 4; ty++) {
        const tileX = left + tx * (w / 12);
        const tileY = bottom - h * 0.35 + ty * (h * 0.35 / 4);
        const shade = (tx + ty) % 2 === 0 ? FLOOR_DARK : FLOOR_STONE;
        g.fillStyle(shade, 0.3);
        g.fillRect(tileX, tileY, w / 12, h * 0.35 / 4);
      }
    }

    // Back wall
    g.fillStyle(STONE_WALL, 1);
    g.fillRect(left, top, w, h * 0.65);

    // Wall texture -- subtle horizontal mortar lines
    g.lineStyle(1, SHADOW_BROWN, 0.15);
    for (let row = 0; row < 8; row++) {
      const y = top + 20 + row * (h * 0.65 / 9);
      g.lineBetween(left + 10, y, right - 10, y);
      // Staggered vertical joints
      const offset = row % 2 === 0 ? 0 : w / 14;
      for (let col = 0; col < 7; col++) {
        const x = left + offset + col * (w / 7);
        g.lineBetween(x, y, x, y + h * 0.65 / 9);
      }
    }

    // Ceiling shadow gradient (darker at top)
    g.fillStyle(DEEP_SHADOW, 0.4);
    g.fillRect(left, top, w, 30);
    g.fillStyle(DEEP_SHADOW, 0.2);
    g.fillRect(left, top + 30, w, 20);

    // Left wall edge shadow
    g.fillStyle(DEEP_SHADOW, 0.3);
    g.fillRect(left, top, 12, h);

    // Right wall edge shadow
    g.fillStyle(DEEP_SHADOW, 0.2);
    g.fillRect(right - 8, top, 8, h);

    // Loft / mezzanine for upgraded homes
    if (hasLoft) {
      const loftY = top + h * 0.28;
      const loftLeft = right - w * 0.45;

      // Loft floor (wooden planks)
      g.fillStyle(WARM_WOOD, 0.9);
      g.fillRect(loftLeft, loftY, right - loftLeft - 8, 8);

      // Loft railing
      g.lineStyle(2, WARM_WOOD, 0.8);
      g.lineBetween(loftLeft, loftY, loftLeft, loftY - 30);
      g.lineBetween(loftLeft, loftY - 30, right - 12, loftY - 30);
      // Railing posts
      for (let i = 0; i < 4; i++) {
        const postX = loftLeft + i * ((right - 12 - loftLeft) / 3);
        g.lineBetween(postX, loftY, postX, loftY - 30);
      }

      // Support beam
      g.fillStyle(WARM_WOOD, 0.7);
      g.fillRect(loftLeft - 4, loftY, 8, bottom - loftY - h * 0.35);

      // Window on loft wall (warm light)
      const winX = right - w * 0.2;
      const winY = loftY - 24;
      g.fillStyle(WINDOW_LIGHT, 0.6);
      g.fillRoundedRect(winX - 12, winY - 14, 24, 20, 3);
      g.lineStyle(2, WARM_WOOD, 0.9);
      g.strokeRoundedRect(winX - 12, winY - 14, 24, 20, 3);
      // Window cross
      g.lineBetween(winX, winY - 14, winX, winY + 6);
      g.lineBetween(winX - 12, winY - 4, winX + 12, winY - 4);
    }
  }

  /** Midground layer: hearth, bed, shelves, door frame. */
  private drawMidgroundFurniture(hasLoft: boolean): void {
    const g = this.midgroundGraphics;
    if (!g) return;
    g.clear();

    const margin = 20;
    const left = margin;
    const right = REFERENCE_WIDTH - margin;
    const bottom = REFERENCE_HEIGHT - margin;
    const floorY = bottom - (REFERENCE_HEIGHT - 2 * margin) * 0.35;

    // ── Hearth / Fireplace ──
    const hearthX = REFERENCE_WIDTH / 2;
    const hearthY = REFERENCE_HEIGHT / 2 - 60;

    // Stone hearth surround
    g.fillStyle(SHADOW_BROWN, 0.9);
    g.fillRoundedRect(hearthX - 34, hearthY - 30, 68, 50, 4);
    g.fillStyle(STONE_WALL, 0.7);
    g.fillRoundedRect(hearthX - 30, hearthY - 26, 60, 42, 3);

    // Fire opening (dark interior)
    g.fillStyle(DEEP_SHADOW, 0.95);
    g.fillRoundedRect(hearthX - 22, hearthY - 18, 44, 30, 2);

    // Fire base (logs)
    g.fillStyle(WARM_WOOD, 0.8);
    g.fillEllipse(hearthX - 8, hearthY + 8, 14, 5);
    g.fillEllipse(hearthX + 8, hearthY + 6, 12, 4);

    // Chimney flue line above hearth
    g.fillStyle(SHADOW_BROWN, 0.6);
    g.fillRect(hearthX - 16, margin + 20, 32, hearthY - 30 - margin - 20);

    // Mantelpiece
    g.fillStyle(WARM_WOOD, 0.85);
    g.fillRect(hearthX - 38, hearthY - 32, 76, 5);

    // ── Bed area (left side) ──
    const bedX = left + 80;
    const bedY = floorY - 6;

    // Bedroll / mattress
    g.fillStyle(BED_FABRIC, 0.8);
    g.fillRoundedRect(bedX - 24, bedY - 14, 48, 18, 4);
    // Blanket fold
    g.fillStyle(BED_BLANKET, 0.7);
    g.fillRoundedRect(bedX - 20, bedY - 10, 30, 12, 3);
    // Pillow
    g.fillStyle(0xd4c4a0, 0.7);
    g.fillEllipse(bedX - 14, bedY - 8, 12, 8);

    // ── Shelf (right wall) ──
    const shelfX = right - 60;
    const shelfBaseY = floorY - 40;

    // Shelf brackets
    g.fillStyle(WARM_WOOD, 0.8);
    g.fillRect(shelfX - 24, shelfBaseY, 48, 4);
    // Second shelf for upgraded homes
    if (hasLoft) {
      g.fillRect(shelfX - 28, shelfBaseY - 30, 56, 4);
      // Items on upper shelf (jars)
      g.fillStyle(0x8ba888, 0.6);
      g.fillRoundedRect(shelfX - 20, shelfBaseY - 44, 10, 14, 2);
      g.fillRoundedRect(shelfX + 2, shelfBaseY - 40, 8, 10, 2);
      g.fillStyle(0xa89070, 0.6);
      g.fillRoundedRect(shelfX + 14, shelfBaseY - 42, 10, 12, 2);
    }

    // Items on lower shelf (bowl, small pot)
    g.fillStyle(0x9a8a6a, 0.5);
    g.fillEllipse(shelfX - 10, shelfBaseY - 4, 12, 5);
    g.fillStyle(0x7a6a5a, 0.5);
    g.fillRoundedRect(shelfX + 6, shelfBaseY - 10, 10, 10, 2);

    // ── Door (bottom center) ──
    const doorX = SPAWN_X;
    const doorY = bottom - 4;

    // Door frame
    g.fillStyle(DOOR_FRAME, 0.9);
    g.fillRect(doorX - 22, doorY - 44, 44, 44);
    // Door interior
    g.fillStyle(DOOR_WOOD, 0.8);
    g.fillRect(doorX - 18, doorY - 40, 36, 38);
    // Door arch
    g.fillStyle(DOOR_FRAME, 0.9);
    g.fillRoundedRect(doorX - 22, doorY - 48, 44, 12, { tl: 8, tr: 8, bl: 0, br: 0 });
    // Outside light spill
    g.fillStyle(WINDOW_LIGHT, 0.15);
    g.fillRect(doorX - 14, doorY - 36, 28, 34);

    // ── Small rug near hearth ──
    g.fillStyle(0x8b5e3c, 0.3);
    g.fillEllipse(hearthX, hearthY + 36, 50, 18);
    g.fillStyle(0x7a4e2c, 0.2);
    g.fillEllipse(hearthX, hearthY + 36, 36, 12);
  }

  /** Foreground elements: subtle vignette edges for depth. */
  private drawForegroundElements(): void {
    const g = this.foregroundGraphics;
    if (!g) return;
    g.clear();

    // Bottom-edge shadow (floor-to-viewer gradient)
    g.fillStyle(DEEP_SHADOW, 0.25);
    g.fillRect(0, REFERENCE_HEIGHT - 30, REFERENCE_WIDTH, 30);

    // Corner shadows for cosy vignette
    g.fillStyle(DEEP_SHADOW, 0.15);
    g.fillTriangle(0, 0, 60, 0, 0, 60);
    g.fillTriangle(REFERENCE_WIDTH, 0, REFERENCE_WIDTH - 60, 0, REFERENCE_WIDTH, 60);
    g.fillTriangle(0, REFERENCE_HEIGHT, 60, REFERENCE_HEIGHT, 0, REFERENCE_HEIGHT - 40);
    g.fillTriangle(REFERENCE_WIDTH, REFERENCE_HEIGHT, REFERENCE_WIDTH - 60, REFERENCE_HEIGHT, REFERENCE_WIDTH, REFERENCE_HEIGHT - 40);
  }

  // ── Fire flicker system ───────────────────────────────────────────────

  /** Animate the hearth fire glow with sine-wave modulation. */
  private updateFireFlicker(time: number): void {
    const g = this.fireGlowGraphics;
    if (!g) return;
    g.clear();

    const hearthX = REFERENCE_WIDTH / 2;
    const hearthY = REFERENCE_HEIGHT / 2 - 60;
    const t = time * 0.001;

    // Primary pulse
    const pulse1 = Math.sin(t * FIRE_PULSE_HZ * Math.PI * 2);
    // Secondary faster flicker for variation
    const pulse2 = Math.sin(t * FIRE_PULSE_SECONDARY_HZ * Math.PI * 2);
    const combined = 0.5 + 0.3 * pulse1 + 0.15 * pulse2;

    // Outer glow (large, subtle)
    const outerAlpha = 0.06 + 0.04 * combined;
    g.fillStyle(HEARTH_GLOW, outerAlpha);
    g.fillCircle(hearthX, hearthY, FIRE_GLOW_RADIUS * 1.5);

    // Mid glow
    const midAlpha = 0.1 + 0.08 * combined;
    g.fillStyle(EMBER_ORANGE, midAlpha);
    g.fillCircle(hearthX, hearthY - 4, FIRE_GLOW_RADIUS);

    // Inner bright glow
    const innerAlpha = 0.2 + 0.15 * combined;
    g.fillStyle(EMBER_YELLOW, innerAlpha);
    g.fillCircle(hearthX, hearthY - 2, FIRE_GLOW_RADIUS * 0.4);

    // Flame tongues (drawn as small jittery shapes)
    const flameCount = 3;
    for (let i = 0; i < flameCount; i++) {
      const flamePhase = t * 3 + i * 2.1;
      const fx = hearthX - 10 + i * 10 + Math.sin(flamePhase) * 3;
      const fy = hearthY - 10 - Math.abs(Math.sin(flamePhase * 1.3)) * 8;
      const flameAlpha = 0.4 + 0.3 * Math.sin(flamePhase);
      g.fillStyle(EMBER_ORANGE, flameAlpha);
      g.fillTriangle(fx - 3, hearthY - 2, fx + 3, hearthY - 2, fx, fy);
      g.fillStyle(EMBER_YELLOW, flameAlpha * 0.7);
      g.fillTriangle(fx - 2, hearthY - 4, fx + 2, hearthY - 4, fx, fy + 3);
    }
  }

  /** Spawn and animate small ember particles drifting upward from the hearth. */
  private updateEmbers(time: number): void {
    const hearthX = REFERENCE_WIDTH / 2;
    const hearthY = REFERENCE_HEIGHT / 2 - 60;

    // Spawn new embers periodically
    if (time - this.lastEmberSpawnTime > EMBER_SPAWN_INTERVAL_MS
        && this.emberPool.length < EMBER_COUNT) {
      this.lastEmberSpawnTime = time;

      const colors = [EMBER_ORANGE, EMBER_YELLOW, EMBER_RED];
      const color = colors[Math.floor(Math.random() * colors.length)];
      const ember = this.add.circle(
        hearthX + (Math.random() - 0.5) * 20,
        hearthY - 10,
        1 + Math.random() * 1.5,
        color,
        0.8,
      );
      ember.setDepth(DEPTH_EMBERS);
      this.emberPool.push(ember);

      // Drift upward with gentle sway
      this.tweens.add({
        targets: ember,
        y: hearthY - 50 - Math.random() * 30,
        x: ember.x + (Math.random() - 0.5) * 30,
        alpha: 0,
        scale: 0.3,
        duration: 1500 + Math.random() * 1000,
        ease: 'Sine.easeOut',
        onComplete: () => {
          const idx = this.emberPool.indexOf(ember);
          if (idx >= 0) this.emberPool.splice(idx, 1);
          ember.destroy();
        },
      });
    }
  }

  // ── Garden plot rendering (SMA-70 F7) ─────────────────────────────

  /** Recompute plot cell states from seed records and redraw. */
  private updateGardenPlots(): void {
    const now = Date.now();
    const activeSeeds = this.seedRecords.filter(s => !s.harvested);

    this.plotCells = activeSeeds.map((seed) =>
      computePlotCell(seed.id, seed.plantedAt, seed.readyAt, now),
    );

    this.renderGardenGraphics();

    this.nearPlotIndex = this.findNearestPlotIndex();
    this.updateGardenTooltip();
  }

  /** Draw all garden plot cells procedurally. */
  private renderGardenGraphics(): void {
    const g = this.gardenGraphics;
    if (!g) return;
    g.clear();

    const totalW = GARDEN_LAYOUT.cols * (GARDEN_LAYOUT.cellW + GARDEN_LAYOUT.gapX) - GARDEN_LAYOUT.gapX + 16;
    const totalH = GARDEN_LAYOUT.rows * (GARDEN_LAYOUT.cellH + GARDEN_LAYOUT.gapY) - GARDEN_LAYOUT.gapY + 16;
    const bgX = GARDEN_LAYOUT.originX - 8;
    const bgY = GARDEN_LAYOUT.originY - 8;

    g.fillStyle(GARDEN_BG_COLOR, 0.45);
    g.fillRoundedRect(bgX, bgY, totalW, totalH, 6);
    g.lineStyle(1, GARDEN_BORDER_COLOR, 0.6);
    g.strokeRoundedRect(bgX, bgY, totalW, totalH, 6);

    for (let i = 0; i < this.plotCells.length; i++) {
      const cell = this.plotCells[i];
      const center = cellCenter(i);
      this.drawPlotCell(g, center.x, center.y, cell.stage);
    }

    // Empty plot outlines for remaining slots
    const maxSlots = GARDEN_LAYOUT.cols * GARDEN_LAYOUT.rows;
    for (let i = this.plotCells.length; i < maxSlots; i++) {
      const center = cellCenter(i);
      g.lineStyle(1, SOIL_COLOR, 0.3);
      g.strokeRect(
        center.x - GARDEN_LAYOUT.cellW / 2,
        center.y - GARDEN_LAYOUT.cellH / 2,
        GARDEN_LAYOUT.cellW,
        GARDEN_LAYOUT.cellH,
      );
    }
  }

  /** Draw a single plot cell based on growth stage. */
  private drawPlotCell(g: Phaser.GameObjects.Graphics, cx: number, cy: number, stage: GrowthStage): void {
    const halfW = GARDEN_LAYOUT.cellW / 2;
    const halfH = GARDEN_LAYOUT.cellH / 2;

    switch (stage) {
      case 'soil': {
        g.fillStyle(SOIL_COLOR, 0.8);
        g.fillEllipse(cx, cy + 4, halfW * 1.4, halfH * 0.6);
        g.fillStyle(0x4a2e14, 0.5);
        g.fillEllipse(cx, cy + 6, halfW * 0.8, halfH * 0.3);
        break;
      }
      case 'sprout': {
        g.fillStyle(SOIL_COLOR, 0.6);
        g.fillEllipse(cx, cy + 8, halfW * 1.2, halfH * 0.4);
        g.lineStyle(2, SPROUT_STEM_COLOR, 1);
        g.lineBetween(cx, cy + 6, cx, cy - 4);
        g.fillStyle(SPROUT_STEM_COLOR, 0.9);
        g.fillTriangle(cx, cy - 2, cx + 5, cy + 1, cx + 1, cy + 3);
        break;
      }
      case 'sapling': {
        g.fillStyle(SOIL_COLOR, 0.5);
        g.fillEllipse(cx, cy + 10, halfW * 1.0, halfH * 0.3);
        g.lineStyle(3, SAPLING_TRUNK_COLOR, 1);
        g.lineBetween(cx, cy + 8, cx, cy - 6);
        g.lineStyle(2, SAPLING_TRUNK_COLOR, 0.8);
        g.lineBetween(cx, cy - 2, cx - 6, cy - 6);
        g.lineBetween(cx, cy - 4, cx + 6, cy - 8);
        g.fillStyle(SAPLING_LEAF_COLOR, 0.8);
        g.fillCircle(cx - 6, cy - 7, 4);
        g.fillCircle(cx + 6, cy - 9, 4);
        g.fillCircle(cx, cy - 8, 5);
        break;
      }
      case 'bloom': {
        g.fillStyle(SOIL_COLOR, 0.4);
        g.fillEllipse(cx, cy + 10, halfW * 0.8, halfH * 0.25);
        g.lineStyle(2, SPROUT_STEM_COLOR, 1);
        g.lineBetween(cx, cy + 8, cx, cy - 8);
        g.fillStyle(SAPLING_LEAF_COLOR, 0.7);
        g.fillEllipse(cx - 4, cy + 2, 4, 2);
        g.fillEllipse(cx + 4, cy - 1, 4, 2);
        const flowerCy = cy - 10;
        const petalDist = 5;
        for (let p = 0; p < 5; p++) {
          const angle = (p / 5) * Math.PI * 2 - Math.PI / 2;
          const px = cx + Math.cos(angle) * petalDist;
          const py = flowerCy + Math.sin(angle) * petalDist;
          g.fillStyle(BLOOM_PETAL_COLOR, 0.85);
          g.fillCircle(px, py, 4);
        }
        g.fillStyle(BLOOM_CENTER_COLOR, 1);
        g.fillCircle(cx, flowerCy, 3);
        break;
      }
    }
  }

  /** Find which plot cell the player is near (for hover/interaction). */
  private findNearestPlotIndex(): number {
    if (!this.playerSprite || this.plotCells.length === 0) return -1;
    const px = this.playerSprite.x;
    const py = this.playerSprite.y;
    const interactDist = 30;

    for (let i = 0; i < this.plotCells.length; i++) {
      const center = cellCenter(i);
      if (Math.abs(px - center.x) < interactDist && Math.abs(py - center.y) < interactDist) {
        return i;
      }
    }
    return -1;
  }

  /** Show/hide garden tooltip based on proximity. */
  private updateGardenTooltip(): void {
    if (!this.gardenTooltip) return;
    if (this.nearPlotIndex < 0 || this.nearPlotIndex >= this.plotCells.length) {
      this.gardenTooltip.setVisible(false);
      return;
    }
    const cell = this.plotCells[this.nearPlotIndex];
    const center = cellCenter(this.nearPlotIndex);
    let text = cell.tooltipText;
    if (cell.harvestReady) {
      text += '\n[E] Harvest';
    }
    this.gardenTooltip.setText(text);
    this.gardenTooltip.setPosition(center.x, center.y - GARDEN_LAYOUT.cellH / 2 - 4);
    this.gardenTooltip.setVisible(true);
  }

  /** Send harvest request to server. */
  private requestHarvest(seedRecordId: string): void {
    if (!this.homeRoom) return;
    this.homeRoom.send(MSG_SEED_HARVEST, { v: 1, seedRecordId });
  }

  /** Handle harvest result from server. */
  private onHarvestResult(result: SeedHarvestResult): void {
    if (!result.harvested) return;

    // Mark the first bloom-ready unharvested seed as harvested locally
    for (let i = 0; i < this.seedRecords.length; i++) {
      const seed = this.seedRecords[i];
      if (!seed.harvested && Date.now() >= seed.readyAt) {
        seed.harvested = true;
        // Find its plot index for animation
        const plotIdx = this.plotCells.findIndex(c => c.seedRecordId === seed.id);
        this.playHarvestAnimation(plotIdx >= 0 ? plotIdx : 0);
        break;
      }
    }
  }

  /** Play the pluck animation: upward float + fade + green particles. */
  private playHarvestAnimation(cellIndex: number): void {
    const center = cellCenter(cellIndex);

    // Temporary bloom graphic that floats up and fades
    const harvestSprite = this.add.graphics();
    harvestSprite.setDepth(12);
    harvestSprite.fillStyle(BLOOM_PETAL_COLOR, 0.85);
    for (let p = 0; p < 5; p++) {
      const angle = (p / 5) * Math.PI * 2 - Math.PI / 2;
      const px = Math.cos(angle) * 5;
      const py = Math.sin(angle) * 5;
      harvestSprite.fillCircle(px, py, 4);
    }
    harvestSprite.fillStyle(BLOOM_CENTER_COLOR, 1);
    harvestSprite.fillCircle(0, 0, 3);
    harvestSprite.setPosition(center.x, center.y - 10);

    this.tweens.add({
      targets: harvestSprite,
      y: center.y - 50,
      alpha: 0,
      duration: 800,
      ease: 'Sine.easeOut',
      onComplete: () => { harvestSprite.destroy(); },
    });

    // Green particle burst
    for (let i = 0; i < 8; i++) {
      const particle = this.add.circle(center.x, center.y - 8, 2, 0x4aa84a, 0.9);
      particle.setDepth(11);
      const angle = (i / 8) * Math.PI * 2;
      const dist = 15 + Math.random() * 10;
      this.tweens.add({
        targets: particle,
        x: center.x + Math.cos(angle) * dist,
        y: center.y - 8 + Math.sin(angle) * dist,
        alpha: 0,
        scale: 0.3,
        duration: 500 + Math.random() * 300,
        ease: 'Sine.easeOut',
        onComplete: () => { particle.destroy(); },
      });
    }
  }

  private destroyGarden(): void {
    this.gardenGraphics?.destroy();
    this.gardenGraphics = null;
    this.gardenTooltip?.destroy();
    this.gardenTooltip = null;
    this.gardenLabel?.destroy();
    this.gardenLabel = null;
    this.seedRecords = [];
    this.plotCells = [];
    this.nearPlotIndex = -1;
  }

  // ── Pet egg visual helpers ─────────────────────────────────────────

  /** Refresh egg visuals when room state changes. */
  private syncEggState(bondingSeconds: number, cueText: string): void {
    this.eggGlowParams = computeEggGlow(bondingSeconds, cueText);
    const hasEgg = this.eggGlowParams !== null;
    this.eggSprite?.setVisible(hasEgg);
    this.eggGlow?.setVisible(hasEgg);
    if (!hasEgg) {
      this.eggTooltip?.setVisible(false);
    }
  }

  /** Drive glow pulse, rocking, and proximity tooltip each frame. */
  private updateEggGlow(time: number): void {
    if (!this.eggGlowParams || !this.eggGlow || !this.eggSprite) return;

    const { pulseHz, glowColor, rocking, cueText } = this.eggGlowParams;

    // Pulse alpha: sine wave from 0.15 to 0.55
    const pulseT = Math.sin(time * 0.001 * pulseHz * Math.PI * 2);
    const glowAlpha = 0.35 + 0.20 * pulseT;

    // Redraw glow circle
    this.eggGlow.clear();
    this.eggGlow.fillStyle(glowColor, glowAlpha);
    this.eggGlow.fillCircle(this.eggBaseX, this.eggBaseY, 18);

    // Rocking at stage >= 5: sway +/-3px at 0.8Hz
    if (rocking) {
      const rockOffset = Math.sin(time * 0.001 * 0.8 * Math.PI * 2) * 3;
      this.eggSprite.x = this.eggBaseX + rockOffset;
    } else {
      this.eggSprite.x = this.eggBaseX;
    }

    // Proximity tooltip
    const nearEgg = this.playerNearEgg();
    if (this.eggTooltip) {
      if (nearEgg && cueText) {
        this.eggTooltip.setText(cueText);
        this.eggTooltip.setVisible(true);
        this.eggTooltip.x = this.eggSprite.x;
      } else {
        this.eggTooltip.setVisible(false);
      }
    }
  }

  private playerNearEgg(): boolean {
    if (!this.playerSprite || !this.eggSprite) return false;
    return Math.abs(this.playerSprite.x - this.eggBaseX) < 30
      && Math.abs(this.playerSprite.y - this.eggBaseY) < 24;
  }

  private playerNearHearth(): boolean {
    if (!this.playerSprite || !this.hearthZone) return false;
    return Math.abs(this.playerSprite.x - this.hearthZone.x) < 40
      && Math.abs(this.playerSprite.y - this.hearthZone.y) < 30;
  }

  private playerOverlapsExit(): boolean {
    if (!this.playerSprite || !this.exitZone) return false;
    const px = this.playerSprite.x;
    const py = this.playerSprite.y;
    const ex = this.exitZone.x;
    const ey = this.exitZone.y;
    return Math.abs(px - ex) < 40 && Math.abs(py - ey) < 20;
  }

  private async joinHomeRoom(): Promise<void> {
    try {
      this.colyseusClient = new Client(COLYSEUS_URL);
      this.homeRoom = await this.colyseusClient.joinOrCreate('home_room', {
        visitorCharacterId: this.characterId,
        ownerCharacterId: this.ownerId,
      });

      this.homeHud?.attach(this.homeRoom);
      this.cookingPanel?.attach(this.homeRoom);
      this.recipeToast = new RecipeDiscoveryToast(this, this.homeRoom);

      this.homeRoom.state.listen('currentStage', (stage: string) => {
        this.renderPaintedInterior(stage);
      });

      const currentStage = (this.homeRoom.state as { currentStage?: string }).currentStage;
      if (currentStage) {
        this.renderPaintedInterior(currentStage);
      }

      // ── Pet egg state sync ──
      type EggState = { eggBondingSeconds?: number; eggDiegeticCue?: string; eggPlacedAt?: number };
      const eggState = this.homeRoom.state as EggState;

      const syncEgg = () => {
        const placed = eggState.eggPlacedAt ?? 0;
        if (placed === 0) {
          this.syncEggState(0, '');
          return;
        }
        this.syncEggState(
          eggState.eggBondingSeconds ?? 0,
          eggState.eggDiegeticCue ?? '',
        );
      };

      // Initial sync
      syncEgg();

      // Listen for changes
      this.homeRoom.state.listen('eggBondingSeconds', () => syncEgg());
      this.homeRoom.state.listen('eggDiegeticCue', () => syncEgg());
      this.homeRoom.state.listen('eggPlacedAt', () => syncEgg());

      // ── Seed snapshot listener (SMA-70 F7) ──
      this.homeRoom.onMessage(MSG_SEED_SNAPSHOT, (evt: SeedSnapshotEvent) => {
        this.seedRecords = evt.seeds ?? [];
      });

      // ── Seed harvest result listener (SMA-70 F7) ──
      this.homeRoom.onMessage(MSG_SEED_HARVEST, (result: SeedHarvestResult) => {
        this.onHarvestResult(result);
      });
    } catch (err) {
      console.warn('[HomeScene] Failed to join home_room:', err);
    }
  }

  private leaveHome(): void {
    // ── Dissolve-out transition ──
    this.tweens.add({
      targets: this.cameras.main,
      alpha: 0,
      duration: DISSOLVE_DURATION_MS,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        this.performLeave();
      },
    });
  }

  private performLeave(): void {
    this.destroyEgg();
    this.destroyGarden();
    this.destroyInterior();
    this.recipeToast?.destroy();
    this.recipeToast = null;
    this.cookingPanel?.destroy();
    this.cookingPanel = null;
    this.homeHud?.destroy();
    this.homeHud = null;
    if (this.homeRoom) {
      this.homeRoom.leave();
      this.homeRoom = null;
    }
    this.colyseusClient = null;

    this.scene.stop(HOME_SCENE_KEY);
    this.scene.wake('OverworldScene', { returnFromHome: true });
  }

  private destroyInterior(): void {
    this.bgWallGraphics?.destroy();
    this.bgWallGraphics = null;
    this.midgroundGraphics?.destroy();
    this.midgroundGraphics = null;
    this.foregroundGraphics?.destroy();
    this.foregroundGraphics = null;
    this.fireGlowGraphics?.destroy();
    this.fireGlowGraphics = null;
    this.stageLabel?.destroy();
    this.stageLabel = null;
    // Clean up ember pool
    for (const ember of this.emberPool) {
      ember.destroy();
    }
    this.emberPool = [];
  }

  private destroyEgg(): void {
    this.eggGlow?.destroy();
    this.eggGlow = null;
    this.eggSprite?.destroy();
    this.eggSprite = null;
    this.eggTooltip?.destroy();
    this.eggTooltip = null;
    this.eggGlowParams = null;
  }

  shutdown(): void {
    this.destroyEgg();
    this.destroyGarden();
    this.destroyInterior();
    this.recipeToast?.destroy();
    this.recipeToast = null;
    this.cookingPanel?.destroy();
    this.cookingPanel = null;
    this.homeHud?.destroy();
    this.homeHud = null;
    if (this.homeRoom) {
      this.homeRoom.leave();
      this.homeRoom = null;
    }
    this.colyseusClient = null;
  }
}
